import AddPlayersPopup from "@components/AddPlayersPopup";
import TrialBaseGameTicketPopup from "@components/TrialBaseGameTicketPopup";
import {
  Rewards,
  RewardTypes,
  UIContextType,
  LaunchStatus,
} from "@playroomkit/pr-wars-data";
import {
  DisableUIOverlayEvent,
  EnableUIOverlayEvent,
  InGameLaunchStatusDataEvent,
  InGameRewardsDataEvent,
  UIContextEvent,
  WarsEvents,
} from "@playroomkit/pr-wars/events";
import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from "react";
import { useManager } from "./WarsContext";
import InvitePlayersPopup from "@components/InvitePlayersPopup";
import LaunchPopup from "@components/LaunchPopup";
import HowToPlayPopup from "@components/HowToPlayPopup";
import ClaimServerRewardPopup from "@components/ClaimServerRewardPopup";
import QuitMenuPopup from "@components/QuitMenuPopup";
import LanguageSelector from "@components/LanguageSelector/LanguageSelector";

const InGamePopupsContext = createContext<{
  showInvitePlayersPopup: () => void;
  closePopup: () => void;
}>({ showInvitePlayersPopup: () => ({}), closePopup: () => ({}) });

export const InGamePopupsProvider = ({ children }: { children: ReactNode }) => {
  const manager = useManager();

  const [selectedPopupType, setSelectedPopupType] =
    useState<UIContextType | null>(null);

  const [inGameRewards, setInGameRewards] =
    useState<{ [key in RewardTypes]?: Rewards }>();

  useEffect(() => {
    const displayInGamePopup = (event: Event) => {
      const e = event as UIContextEvent;

      const hasAllAccess = manager.getMyPlayer()?.allAccess;

      switch (e.contextType) {
        case UIContextType.HOW_TO_PLAY:
        case UIContextType.INVITE_PLAYERS:
        case UIContextType.ADD_PLAYERS:
        case UIContextType.QUIT_MENU:
        case UIContextType.LANGUAGE_SELECTOR: {
          setSelectedPopupType(e.contextType as UIContextType);
          break;
        }
        case UIContextType.LAUNCH_STATUS: {
          const launchStatus = manager.getLaunchStatus();
          if (!launchStatus?.isLaunched) {
            document.dispatchEvent(new EnableUIOverlayEvent());
            setSelectedPopupType(e.contextType as UIContextType);
          }
          break;
        }
        case UIContextType.JOIN_SERVER: {
            setSelectedPopupType(e.contextType as UIContextType);
          break;
        }
        case UIContextType.ONBOARDING_TRIAL_TICKET: {
          const onboardingTrialTicket = inGameRewards?.ONBOARDING_TRIAL_TICKET;
          if (
            onboardingTrialTicket &&
            !onboardingTrialTicket?.claimed &&
            !hasAllAccess
          ) {
            setSelectedPopupType(e.contextType as UIContextType);
          }
          break;
        }
        default:
          break;
      }
    };

    const handleInGameRewardsData = (event: Event) => {
      const e = event as InGameRewardsDataEvent;

      const rewards = { ...inGameRewards };
      e.data.forEach((reward) => {
        rewards[reward.key] = reward;
      });

      setInGameRewards(rewards);
    };

    // UI Context
    document.addEventListener(WarsEvents.UIContextEvent, displayInGamePopup);

    // In-game Rewards
    document.addEventListener(
      WarsEvents.InGameRewardsDataEvent,
      handleInGameRewardsData
    );

    return () => {
      document.removeEventListener(
        WarsEvents.UIContextEvent,
        displayInGamePopup
      );

      document.removeEventListener(
        WarsEvents.InGameRewardsDataEvent,
        handleInGameRewardsData
      );
    };
  }, [inGameRewards, manager]);

  const showInvitePlayersPopup = () => {
    setSelectedPopupType(UIContextType.ADD_PLAYERS);
  };

  const closePopup = () => {
    document.dispatchEvent(new DisableUIOverlayEvent());
    setSelectedPopupType(null);
  };

  return (
    <InGamePopupsContext.Provider
      value={{ showInvitePlayersPopup, closePopup }}
    >
      {children}
      {selectedPopupType === UIContextType.ADD_PLAYERS && (
        <AddPlayersPopup closePopup={closePopup} />
      )}
      {selectedPopupType === UIContextType.ONBOARDING_TRIAL_TICKET && (
        <TrialBaseGameTicketPopup
          rewardId={inGameRewards?.ONBOARDING_TRIAL_TICKET?.id}
          closePopup={closePopup}
        />
      )}
      {selectedPopupType === UIContextType.INVITE_PLAYERS && (
        <InvitePlayersPopup closePopup={closePopup} />
      )}
      {selectedPopupType === UIContextType.LAUNCH_STATUS && (
        <LaunchPopup closePopup={closePopup} />
      )}
      {selectedPopupType === UIContextType.JOIN_SERVER && (
        <ClaimServerRewardPopup closePopup={closePopup} />
      )}
      {selectedPopupType === UIContextType.HOW_TO_PLAY && (
        <HowToPlayPopup closePopup={closePopup} />
      )}
      {selectedPopupType === UIContextType.QUIT_MENU && (
        <QuitMenuPopup closePopup={closePopup} />
      )}
      {selectedPopupType === UIContextType.LANGUAGE_SELECTOR && (
        <LanguageSelector closePopup={closePopup} />
      )}
    </InGamePopupsContext.Provider>
  );
};

export const useInGamePopupsState = () => {
  const context = useContext(InGamePopupsContext);
  if (!context) {
    throw new Error(
      "useInGamePopupsState must be used within a InGamePopupsProvider"
    );
  }
  return context;
};
